import { useQuery } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";

import { apiGet } from "../../api/client";
import type { FeedResponse } from "../../api/legacyTypes";
import { AppShell } from "../../app/AppShell";
import { ResultsFeed } from "../../components/ResultsFeed";
import { gameModule } from "../../games/registry";

export function FeedPage() {
  const [params, setParams] = useSearchParams();
  const game = params.get("game") || "all";
  const tournamentId = params.get("tournament") || "";
  const tier = params.get("tier") || "all";
  const followingOnly = params.get("scope") !== "all";
  const query = useQuery({
    queryKey: ["results-feed", followingOnly ? "following" : "all"],
    queryFn: ({ signal }) => apiGet<FeedResponse>(followingOnly ? "/api/v1/feed" : "/api/v1/tournaments", signal),
  });
  const filteredTournaments = (query.data?.tournaments ?? []).filter((tournament) => (game === "all" || tournament.wiki === game) && (tier === "all" || tournament.tier === tier));
  const tournaments = filteredTournaments.filter((tournament) => !tournamentId || tournament.id === tournamentId);
  const selectedTournament = (query.data?.tournaments ?? []).find((tournament) => tournament.id === tournamentId);
  const changeGame = (nextGame: string) => {
    const next = new URLSearchParams(params);
    if (nextGame === "all") next.delete("game"); else next.set("game", nextGame);
    next.delete("tournament");
    setParams(next);
  };
  const changeTournament = (nextTournament: string) => {
    const next = new URLSearchParams(params);
    if (nextTournament) next.set("tournament", nextTournament); else next.delete("tournament");
    setParams(next);
  };
  const changeTier = (nextTier: string) => {
    const next = new URLSearchParams(params);
    if (nextTier === "all") next.delete("tier"); else next.set("tier", nextTier);
    next.delete("tournament");
    setParams(next);
  };
  const changeScope = (nextFollowingOnly: boolean) => {
    const next = new URLSearchParams(params);
    if (nextFollowingOnly) next.delete("scope"); else next.set("scope", "all");
    next.delete("tournament");
    setParams(next);
  };

  return (
    <AppShell selectedGame={game} onGameChange={changeGame}>
      <section className="page-heading">
        <p className="eyebrow">Results inbox</p>
        <h1>{selectedTournament ? `${selectedTournament.name} feed` : game === "all" ? "Results feed" : `${gameModule(game)?.name ?? "Game"} results`}</h1>
        <p>Resume at your last-seen result and move upward through new results without the feed reshuffling underneath you.</p>
      </section>
      <div className="results-feed-toolbar"><label className="results-following-filter"><input type="checkbox" checked={followingOnly} onChange={(event) => changeScope(event.target.checked)} /><span>Following only</span></label><label><span>Tier</span><select className="results-tier-filter" aria-label="Tournament tier" value={tier} onChange={(event) => changeTier(event.target.value)}><option value="all">All tiers</option><option value="1">Tier 1</option><option value="2">Tier 2</option><option value="3">Tier 3</option><option value="4">Tier 4</option></select></label>{!query.isPending && !query.isError && <label><span>Tournament</span><select aria-label="Tournament feed" value={tournamentId} onChange={(event) => changeTournament(event.target.value)}><option value="">{followingOnly ? "All followed tournaments" : "All tournaments"}</option>{filteredTournaments.map((tournament) => <option value={tournament.id} key={tournament.id}>{tournament.name}</option>)}</select></label>}</div>
      {query.isPending && <p className="state-panel">Loading results…</p>}
      {query.isError && <p className="state-panel state-error">The results feed could not be loaded.</p>}
      {!query.isPending && !query.isError && tournaments.length === 0 && <p className="state-panel">No tournaments match these filters.</p>}
      {!query.isPending && !query.isError && tournaments.length > 0 && <ResultsFeed key={`${followingOnly}:${game}:${tier}:${tournamentId}`} tournaments={tournaments} />}
    </AppShell>
  );
}
